// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Cargas.tsx
//  Listagem de Cargas / Vendas Externas por Rota
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Trash2, Truck, PackageCheck, CheckCircle2,
  Eye, Pencil, ChevronLeft, ChevronRight, MapPin, RotateCcw,
} from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { useCarga } from '@/features/operacional/hooks/useCarga';
import { calcularStatusCarga } from '@/features/operacional/types';
import type { CargaResumo, StatusCarga } from '@/features/operacional/types';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const statusConfig: Record<StatusCarga, { label: string; className: string; icon: typeof Truck }> = {
  montagem:   { label: 'Montagem',  className: 'bg-blue-500/10 text-blue-500',    icon: PackageCheck },
  em_rota:    { label: 'Em Rota',   className: 'bg-orange-500/10 text-orange-500', icon: Truck        },
  retornada:  { label: 'Retornada', className: 'bg-purple-500/10 text-purple-500', icon: RotateCcw    },
  finalizada: { label: 'Finalizada',className: 'bg-green-500/10 text-green-500',  icon: CheckCircle2 },
};

const PAGE_SIZES = [10, 20, 50, 100] as const;

const Cargas = () => {
  const navigate = useNavigate();
  const [searchTerm,    setSearchTerm]    = useState('');
  const [statusFiltro,  setStatusFiltro]  = useState<string>('todos');
  const [deletingId,    setDeletingId]    = useState<number | null>(null);
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);

  // Abre nos últimos 30 dias (pela montagem), mais recentes primeiro.
  const periodo   = usePeriodo('30dias', () => setPagina(1));
  const ordenacao = useOrdenacao<'montagem' | 'numero' | 'motorista' | 'valor'>('montagem', true, () => setPagina(1));
  // Busca e status vão ao servidor (antes filtravam só a página carregada): espera a digitação parar.
  const buscaEnviada = useDebounce(searchTerm.trim(), 400);

  const { cargas, total, totalPaginas, resumo, isLoading, isFetching, deleteCarga } =
    useCarga({
      pagina, tamanhoPagina,
      filtros: {
        busca: buscaEnviada, status: statusFiltro, ...periodo.intervalo,
        ordenarPor: ordenacao.ordenarPor, decrescente: ordenacao.decrescente,
      },
    });

  // KPIs do servidor: busca + período inteiros, não só a página.
  const totalMontagem    = resumo?.montagem    ?? 0;
  const totalEmRota      = resumo?.emRota      ?? 0;
  const totalRetornadas  = resumo?.retornadas  ?? 0;
  const totalFinalizadas = resumo?.finalizadas ?? 0;

  const firstItem = (pagina - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pagina * tamanhoPagina, total);

  const handlePageSizeChange = (value: string) => {
    setTamanhoPagina(Number(value)); setPagina(1);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Cargas / Rota</h1>
          <p className="text-muted-foreground mt-1">Gerencie as vendas externas por rota</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/operacional/cargas/nova')}>
          <Plus className="h-4 w-4" />Nova Carga
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <PackageCheck className="h-4 w-4" />Em Montagem
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{totalMontagem}</div>
            <p className="text-xs text-muted-foreground mt-1">Aguardando saída</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Truck className="h-4 w-4" />Em Rota
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">{totalEmRota}</div>
            <p className="text-xs text-muted-foreground mt-1">Em campo</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <RotateCcw className="h-4 w-4" />Retornadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-500">{totalRetornadas}</div>
            <p className="text-xs text-muted-foreground mt-1">Aguardando conferência</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />Finalizadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{totalFinalizadas}</div>
            <p className="text-xs text-muted-foreground mt-1">Conferidas e fechadas</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <CardTitle>Listagem de Cargas</CardTitle>
            <div className="flex flex-col sm:flex-row sm:flex-wrap md:justify-end gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Nº ou motorista..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPagina(1); }}
                  className="pl-9" />
              </div>
              <PeriodoFiltro estado={periodo} rotuloData="Montagem" />
              <Select value={statusFiltro} onValueChange={(v) => { setStatusFiltro(v); setPagina(1); }}>
                <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="montagem">Em Montagem</SelectItem>
                  <SelectItem value="em_rota">Em Rota</SelectItem>
                  <SelectItem value="retornada">Retornada</SelectItem>
                  <SelectItem value="finalizada">Finalizada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>Nº</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="motorista" ordenacao={ordenacao} decrescentePadrao={false}>Motorista</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="montagem" ordenacao={ordenacao}>Montagem</CabecalhoOrdenavel>
                  <TableHead>Saída</TableHead>
                  <TableHead>Retorno</TableHead>
                  <CabecalhoOrdenavel coluna="valor" ordenacao={ordenacao} className="text-right">Valor Total</CabecalhoOrdenavel>
                  <TableHead className="text-right">Divergência</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
                ) : cargas.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    {periodo.periodo !== 'todos'
                      ? 'Nenhuma carga neste período — amplie o período para ver as anteriores'
                      : 'Nenhuma carga encontrada'}
                  </TableCell></TableRow>
                ) : (
                  cargas.map((c: CargaResumo) => {
                    const status = calcularStatusCarga(c);
                    const cfg    = statusConfig[status];
                    const Icon   = cfg.icon;
                    const podeEditar = !c.isFinalizado;
                    return (
                      <TableRow key={c.id}>
                        <TableCell className="font-medium">#{c.numeroCarga}</TableCell>
                        <TableCell>
                          {c.motorista
                            ? <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-muted-foreground" />{c.motorista}</span>
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>{new Date(c.dtMontagem).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell>
                          {c.dtSaida
                            ? new Date(c.dtSaida).toLocaleDateString('pt-BR')
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          {c.dtRetorno
                            ? new Date(c.dtRetorno).toLocaleDateString('pt-BR')
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {(c.valorTotalCarga ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </TableCell>
                        <TableCell className="text-right">
                          {c.valorDivergencia !== 0
                            ? <span className="text-destructive font-medium">
                                {c.valorDivergencia.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                              </span>
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          <Badge className={cfg.className}>
                            <span className="flex items-center gap-1"><Icon className="h-3 w-3" />{cfg.label}</span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {podeEditar && (
                              <Button size="sm" variant="ghost"
                                onClick={() => navigate(`/operacional/cargas/${c.id}`)} title="Editar">
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" title="Ver detalhes"
                              onClick={() => navigate(`/operacional/cargas/${c.id}`)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            {podeEditar && (
                              <Button size="sm" variant="ghost" onClick={() => setDeletingId(c.id)} title="Excluir">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0 ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
              {isFetching && !isLoading && <span className="ml-2 text-xs">(atualizando...)</span>}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}><ChevronLeft className="h-4 w-4" /></Button>
                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)         page = i + 1;
                  else if (pagina <= 3)                page = i + 1;
                  else if (pagina >= totalPaginas - 2) page = totalPaginas - 4 + i;
                  else                                 page = pagina - 2 + i;
                  return (
                    <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>{page}</Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}><ChevronRight className="h-4 w-4" /></Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={!!deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        onConfirm={() => { if (deletingId) { deleteCarga(deletingId); setDeletingId(null); } }}
        title="Excluir Carga"
        description="Tem certeza que deseja excluir esta carga?"
      />
    </div>
  );
};

export default Cargas;
